import React, { useState, useEffect, useRef } from 'react';
import { ListCompareService, CompareMode } from '../../lib/services/listCompareService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import './ListCompare.css';

export const ListCompare: React.FC = () => {
  const [leftList, setLeftList] = useState('');
  const [rightList, setRightList] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<CompareMode>('difference');
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const processingRef = useRef<string>('');

  const handleCompare = async () => {
    if (!leftList.trim() && !rightList.trim()) {
      setOutput('');
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const processingKey = `${mode}-${leftList.substring(0, 50)}-${rightList.substring(0, 50)}`;
    processingRef.current = processingKey;

    try {
      const result = await ListCompareService.compare(leftList, rightList, mode);

      // Check if this is still the latest operation
      if (processingRef.current === processingKey) {
        const formatted = ListCompareService.formatResult(result.outputData as any);
        setOutput(formatted);
        setError(null);
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, 'list-compare' as any);
        setError(errorMessage);
        setOutput('');
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (leftList || rightList) {
      const timer = setTimeout(() => {
        handleCompare();
      }, 300); // Debounce for real-time processing

      return () => clearTimeout(timer);
    } else {
      setOutput('');
      setError(null);
      setIsLoading(false);
    }
  }, [leftList, rightList, mode]);

  const handleCopy = async () => {
    if (!output) return;

    try {
      const success = await ClipboardUtils.copyText(output);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Result';
    }
  };

  const getButtonClassName = () => {
    const baseClass = 'copy-button';
    if (!output) return `${baseClass} copy-idle`;
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  return (
    <div className="list-compare">
      <noscript>
        <div className="fallback-form">
          <h3>JavaScript Required</h3>
          <p>List comparison requires JavaScript. Please enable JavaScript to use this tool.</p>
        </div>
      </noscript>

      <div style={{ marginBottom: '2rem' }}>
        <h1>List Compare</h1>
        <p>
          Compare two lists to find differences, intersection, or union.
          Enter one item per line in each list.
        </p>
      </div>

      <div className="encoder-container">
        <div className="form-group">
          <label>
            Comparison Mode:
          </label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as CompareMode)}
            data-testid="mode-select"
          >
            <option value="difference">Difference (A-B, B-A)</option>
            <option value="intersection">Intersection (A∩B)</option>
            <option value="union">Union (A∪B)</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            List A (one item per line):
          </label>
          <textarea
            value={leftList}
            onChange={(e) => setLeftList(e.target.value)}
            placeholder="Enter first list items..."
            data-testid="left-list"
          />
        </div>

        <div className="form-group">
          <label>
            List B (one item per line):
          </label>
          <textarea
            value={rightList}
            onChange={(e) => setRightList(e.target.value)}
            placeholder="Enter second list items..."
            data-testid="right-list"
          />
        </div>

        {error && (
          <div className="status-message status-error">
            {error}
          </div>
        )}

        <div className="form-group">
          <label>
            Comparison Result:
          </label>
          <textarea
            value={output}
            readOnly
            placeholder={isLoading ? 'Processing...' : 'Comparison result will appear here...'}
            className={isLoading ? 'loading' : ''}
            data-testid="output-text"
            style={{ height: '200px' }}
          />
          {isLoading && (
            <div className="loading-indicator">Processing...</div>
          )}
        </div>

        <div className="button-container">
          <button
            ref={copyButtonRef}
            onClick={handleCopy}
            disabled={!output}
            className={getButtonClassName()}
            data-testid="copy-button"
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};
